import styled from "@emotion/styled";
import { Typography } from "@mui/material";
import { Box } from "../../UI/Box";
import { Paper } from "../../UI/Paper";
import { Grid } from "../../UI/Grid";
import {
  COLOR_ATHENS_GRAY,
  COLOR_OCEAN_GREEN,
  COLOR_SCIENCE_BLUE,
  COLOR_WHITE,
  COLOR_TREE_POPPY
} from "../../constants/styles";

export const StepContainer = styled(Paper)`
  padding: 24px;
  margin-bottom: 24px;
`;

export const StepHeader = styled(Box)`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`;

export const StepNumber = styled(Box)`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${COLOR_SCIENCE_BLUE};
  color: ${COLOR_WHITE};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  font-weight: 600;
`;

export const StepTitle = styled(Box)`
  font-size: 18px;
  font-weight: 600;
`;

export const StepContent = styled(Box)`
  margin-left: 48px;
`;

export const StepActions = styled(Box)`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
`;

export const CompletedStep = styled(Box)`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`;

export const CompletedStepNumber = styled(Box)`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${COLOR_OCEAN_GREEN};
  color: ${COLOR_WHITE};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  font-weight: 600;
`;

export const CompletedStepTitle = styled(Box)`
  font-size: 18px;
  font-weight: 600;
  color: ${COLOR_OCEAN_GREEN};
`;

export const CompletedStepContent = styled(Box)`
  margin-left: 48px;
  margin-bottom: 24px;
`;

export const SummaryContainer = styled(Grid)`
  background-color: ${COLOR_ATHENS_GRAY};
  padding: 16px;
  border-radius: 4px;
  margin-bottom: 16px;
`;

export const SummaryLabel = styled(Box)`
  font-weight: 500;
  margin-bottom: 4px;
  font-size: 12px;
  color: #757575;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const SummaryValue = styled(Box)`
  margin-bottom: 12px;
  font-size: 14px;
  font-weight: 500;
  color: #212121;
`;

export const ExportTitle = styled(Typography)`
  font-size: 16px !important;
  font-weight: 500 !important;
  color: #1e3a5f !important;
  margin-bottom: 12px !important;
  line-height: 1.4 !important;
`;

export const ExportStatus = styled(Typography)<{ status?: string }>`
  font-size: 12px !important;
  font-weight: 500 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  margin-bottom: 16px !important;
  color: ${({ status }) => {
    switch (status) {
      case 'COMPLETED':
        return '#4CAF50 !important'; // Green
      case 'IN PROGRESS':
        return '#FF9800 !important'; // Orange
      case 'FAILED':
        return '#F44336 !important'; // Red
      default:
        return '#757575 !important'; // Default gray
    }
  }};
`;

// Status badge with colored background pill
export const StatusBadge = styled(Box)<{ variant?: 'success' | 'error' | 'warning' | 'neutral' }>`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${({ variant }) => {
    switch (variant) {
      case 'success':
        return '#e8f5e9';
      case 'error':
        return '#ffebee';
      case 'warning':
        return '#fff3e0';
      case 'neutral':
      default:
        return '#f5f5f5';
    }
  }};
  color: ${({ variant }) => {
    switch (variant) {
      case 'success':
        return '#2e7d32';
      case 'error':
        return '#c62828';
      case 'warning':
        return '#ef6c00';
      case 'neutral':
      default:
        return '#616161';
    }
  }};
`;

export const WarningBox = styled(Box)`
  background-color: #FFF8E1;
  border-left: 4px solid ${COLOR_TREE_POPPY};
  padding: 12px 16px;
  margin-bottom: 24px;
  border-radius: 4px;
  max-width: 800px;
`;

export const WarningList = styled.ul`
  margin: 0;
  padding-left: 16px;
  font-size: 13px;
  color: #424242;

  & > li {
    margin-bottom: 4px;
  }

  & > li:last-child {
    margin-bottom: 0;
  }
`;

export const StepperContainer = styled(Box)<{ progressPercentage?: number }>`
  display: flex;
  justify-content: space-between;
  position: relative;
  width: 550px;
  margin-left: 0;

  &::before {
    content: '';
    position: absolute;
    top: 8px;
    left: 40px;
    right: 40px;
    height: 3px;
    background-color: #E3E3E3;
    z-index: 0;
  }

  &::after {
    content: '';
    position: absolute;
    top: 8px;
    left: 40px;
    height: 3px;
    background-color: #2167e2;
    z-index: 1;
    width: ${props => {
      const totalWidth = 470; // 550px - 80px (40px on each side)
      return `${(props.progressPercentage || 0) * totalWidth / 100}px`;
    }};
    transition: width 0.3s ease;
  }
`;

export const StepItem = styled(Box)<{ active?: boolean; completed?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
  width: 90px;

  & > .step-circle {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 6px;
    background-color: ${props =>
      props.completed ? '#2167e2' :
      props.active ? '#2167e2' :
      '#E3E3E3'};
    color: ${props =>
      (props.completed || props.active) ? COLOR_WHITE :
      '#757575'};
    border: 1px solid ${props =>
      props.completed ? '#4CAF50' :
      props.active ? '#2167e2' :
      '#BDBDBD'};
    font-size: 11px;
    font-weight: 500;
  }

  & > .step-label {
    color: ${props =>
      props.completed ? '#2167e2' :
      props.active ? '#2167e2' :
      '#757575'};
    font-weight: ${props => (props.active || props.completed) ? '600' : '500'};
    text-align: center;
    font-size: 11px;
    line-height: 1.2;
    max-width: 90px;
    white-space: pre-line;
  }
`;

export const NoMatchingRoundsContainer = styled(Box)`
  background-color: #F5F5F5;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 24px;
  max-width: 800px;
`;

export const ArchivedSection = styled(Box)`
  margin-top: 32px;
  max-width: 800px;
`;

export const ArchivedHeader = styled(Box)`
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-bottom: 16px;
  color: #424242;
  font-size: 14px;

  & > svg {
    margin-left: 4px;
    transition: transform 0.2s ease;
    font-size: 18px;
  }

  &.expanded > svg {
    transform: rotate(180deg);
  }
`;

export const WizardContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #F5F5F5;
  padding: 16px;
  border-radius: 4px;
`;

export const SelectionContainer = styled(Box)`
  padding: 24px;
  background-color: ${COLOR_WHITE};
  border-radius: 4px;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.05);
  height: 100%;
`;

export const GroupCard = styled(Box)`
  padding: 12px;
  border: 1px solid #E0E0E0;
  border-radius: 4px;
  margin-bottom: 12px;
  background-color: white;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const GroupsContainer = styled(Box)`
  max-height: 300px;
  overflow-y: auto;
  margin-bottom: 16px;
  padding-right: 4px;
`;

export const ExportSummary = styled(Box)`
  background-color: ${COLOR_WHITE};
  border: 1px solid #E0E0E0;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease;
  min-height: 120px;
  width: 260px;
  display: flex;
  flex-direction: column;

  &:hover {
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.12);
    transform: translateY(-1px);
  }
`;

// Export card for the redesigned previous exports section
export const ExportCard = styled(Box)`
  background-color: ${COLOR_WHITE};
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 240px;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.04);
`;

// Collapsible section header
export const CollapsibleHeader = styled(Box)`
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #1e3a5f;
  margin-bottom: 16px;

  & > svg {
    margin-left: 4px;
    transition: transform 0.2s ease;
    font-size: 18px;
  }

  &.collapsed > svg {
    transform: rotate(180deg);
  }
`;

// Summary stat card for export details
export const StatCard = styled(Box)`
  background-color: ${COLOR_WHITE};
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  flex: 1;
  min-width: 180px;
  max-width: 280px;
`;
